"use client";

import { c as _c } from "react/compiler-runtime";
import EnsAddressInput from "@/components/utils/input/ens-address/EnsAddressInput";
import React, { useCallback, useState } from "react";
import { TraitWrapper } from "../../traits/TraitWrapper";
import type { PaymentInfo } from "../types/OperationalData";
import FormSection from "../ui/FormSection";
import { validateStrictAddress } from "../utils/addressValidation";
interface PaymentConfigProps {
  readonly paymentInfo: PaymentInfo;
  readonly onPaymentInfoChange: (paymentInfo: PaymentInfo) => void;
}
const PaymentConfig: React.FC<PaymentConfigProps> = t0 => {
  const $ = _c(36);
  const {
    paymentInfo,
    onPaymentInfoChange
  } = t0;
  const [isLoading, setIsLoading] = useState(false);
  const [hasEnsError, setHasEnsError] = useState(false);
  let t1;
  if ($[0] !== hasEnsError) {
    t1 = address => {
      if (hasEnsError) {
        return "Could not resolve ENS name";
      }
      if (!address) {
        return null;
      }
      return validateStrictAddress(address) ? null : "Invalid address (0x... 42 chars)";
    };
    $[0] = hasEnsError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getAddressError = t1;
  let t2;
  if ($[2] !== onPaymentInfoChange || $[3] !== paymentInfo) {
    t2 = resolvedAddress => {
      onPaymentInfoChange({
        ...paymentInfo,
        payment_address: resolvedAddress
      });
    };
    $[2] = onPaymentInfoChange;
    $[3] = paymentInfo;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleAddressChange = t2;
  let t3;
  if ($[5] !== onPaymentInfoChange || $[6] !== paymentInfo) {
    t3 = e => {
      const checked = e.target.checked;
      onPaymentInfoChange({
        ...paymentInfo,
        has_designated_payee: checked,
        designated_payee_name: checked ? paymentInfo.designated_payee_name ?? "" : ""
      });
    };
    $[5] = onPaymentInfoChange;
    $[6] = paymentInfo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleDesignatedPayeeToggle = t3;
  let t4;
  if ($[8] !== onPaymentInfoChange || $[9] !== paymentInfo) {
    t4 = e_0 => {
      onPaymentInfoChange({
        ...paymentInfo,
        designated_payee_name: e_0.target.value
      });
    };
    $[8] = onPaymentInfoChange;
    $[9] = paymentInfo;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleDesignatedPayeeNameChange = t4;
  const address_0 = paymentInfo.payment_address ?? "";
  const hasDesignatedPayee = paymentInfo.has_designated_payee ?? false;
  const designatedPayeeName = paymentInfo.designated_payee_name ?? "";
  const addressError = getAddressError(address_0);
  const isAddressValid = !!address_0 && !addressError && !isLoading;
  const designatedPayeeNameError = hasDesignatedPayee && !designatedPayeeName.trim() ? "Designated payee name is required" : null;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="-tw-mt-1 tw-mb-3 tw-text-sm tw-text-iron-500">Address to receive split of minting proceeds.</p>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== handleDesignatedPayeeToggle || $[13] !== hasDesignatedPayee) {
    t6 = <input type="checkbox" checked={hasDesignatedPayee} onChange={handleDesignatedPayeeToggle} aria-label="Designated Payee" className="tw-h-4 tw-w-4 tw-cursor-pointer tw-rounded tw-border-iron-700 tw-bg-iron-900 tw-text-primary-400 focus:tw-ring-primary-400 focus:tw-ring-offset-0" />;
    $[12] = handleDesignatedPayeeToggle;
    $[13] = hasDesignatedPayee;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="tw-text-sm"><span className="tw-text-iron-300">Designated Payee</span><span className="tw-ml-1 tw-text-iron-500">— proceeds will be paid to a third party</span></span>;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t6) {
    t8 = <div className="tw-mb-5"><label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3">{t6}{t7}</label></div>;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== designatedPayeeName || $[19] !== designatedPayeeNameError || $[20] !== handleDesignatedPayeeNameChange || $[21] !== hasDesignatedPayee) {
    t9 = hasDesignatedPayee && <TraitWrapper label="Designated Payee Name *" id="designated-payee-name" error={designatedPayeeNameError} isFieldFilled={!!designatedPayeeName.trim()} className="tw-pb-8"><input type="text" value={designatedPayeeName} onChange={handleDesignatedPayeeNameChange} placeholder="Enter designated payee name" autoFocus={true} className={`tw-form-input tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-text-iron-100 tw-outline-none tw-ring-1 focus:tw-bg-iron-900 focus:tw-text-iron-100 ${designatedPayeeNameError ? "tw-ring-red" : "tw-ring-iron-700"} focus:tw-ring-primary-400`} /></TraitWrapper>;
    $[18] = designatedPayeeName;
    $[19] = designatedPayeeNameError;
    $[20] = handleDesignatedPayeeNameChange;
    $[21] = hasDesignatedPayee;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = hasDesignatedPayee ? "Designated Payee Address *" : "Payment Address *";
  const t11 = `tw-form-input tw-w-full tw-truncate tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-3 tw-pl-4 tw-pr-11 tw-text-sm tw-text-iron-100 tw-outline-none tw-ring-1 focus:tw-bg-iron-900 focus:tw-text-iron-100 ${addressError ? "tw-ring-red" : "tw-ring-iron-700"} focus:tw-ring-primary-400`;
  let t12;
  if ($[23] !== address_0 || $[24] !== handleAddressChange || $[25] !== t11) {
    t12 = <EnsAddressInput value={address_0} placeholder="0x... or ENS" onAddressChange={handleAddressChange} onLoadingChange={setIsLoading} onError={setHasEnsError} className={t11} />;
    $[23] = address_0;
    $[24] = handleAddressChange;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== addressError || $[28] !== isAddressValid || $[29] !== t10 || $[30] !== t12) {
    t13 = <TraitWrapper label={t10} id="payment-address" error={addressError} isFieldFilled={isAddressValid} className="tw-pb-0">{t12}</TraitWrapper>;
    $[27] = addressError;
    $[28] = isAddressValid;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t8 || $[34] !== t9) {
    t14 = <FormSection title="Payment">{t5}{t8}{t9}{t13}</FormSection>;
    $[32] = t13;
    $[33] = t8;
    $[34] = t9;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};
export default React.memo(PaymentConfig);